import { zodResolver } from '@hookform/resolvers/zod'
import { Check, ListChecks, Loader2 } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { STAGE_STATUSES, type Installation, type Project, type Stage } from '../../types'
import { toastError, useStaffLookup } from '../../ui'
import { useInstallationActions } from '../../workApi'

type Props = { open: boolean; project: Project; installation: Installation; stage: Stage | null; onOpenChange: (v: boolean) => void }

export function StageFormModal({ open, onOpenChange, ...rest }: Props) {
  return open ? <StageForm key={rest.stage?.id ?? 'new'} {...rest} onDone={() => onOpenChange(false)} /> : null
}

function StageForm({ project, installation, stage, onDone }: Omit<Props, 'open' | 'onOpenChange'> & { onDone: () => void }) {
  const { t } = useTranslation()
  const { saveStage } = useInstallationActions(project.id, installation.id)
  const { nameOf } = useStaffLookup()

  const schema = z.object({
    name: z.string().trim().min(2, t('projects.install.form.stageNameRequired')).max(60),
    description: z.string().trim().max(160),
    ownerId: z.string(),
    plannedDate: z.string().min(1, t('projects.form.dateRequired')),
    completion: z.coerce.number<string | number>().int().min(0, t('projects.install.form.completionInvalid')).max(100, t('projects.install.form.completionInvalid')),
    status: z.enum(STAGE_STATUSES),
    taskIds: z.array(z.string()),
  })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: stage?.name ?? '',
      description: stage?.description ?? '',
      ownerId: stage?.ownerId ?? '',
      plannedDate: stage?.plannedDate ?? installation.startDate,
      completion: stage?.completion ?? 0,
      status: stage?.status ?? 'pending',
      taskIds: stage?.taskIds ?? [],
    },
  })
  const { errors } = form.formState
  const candidates = project.tasks.filter((x) => x.installationId === installation.id)

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await saveStage.mutateAsync({ id: stage?.id, input: { ...v, ownerId: v.ownerId || null } })
      toast.success(t(stage ? 'projects.install.stageUpdated' : 'projects.install.stageCreated'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!saveStage.isPending}
      icon={<ListChecks />}
      title={t(stage ? 'projects.install.form.editStage' : 'projects.install.form.createStage')}
      description={t('projects.install.form.stageHint', { name: installation.customer })}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={saveStage.isPending}>
            {saveStage.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="sg-name">{t('projects.install.stageCols.name')}</Label>
          <Input id="sg-name" aria-invalid={!!errors.name} {...form.register('name')} />
          {errors.name ? <p className="text-xs text-destructive">{errors.name.message}</p> : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="sg-owner">{t('projects.install.columns.owner')}</Label>
          <FormSelect id="sg-owner" control={form.control} name="ownerId" options={[{ value: '', label: t('projects.work.unassigned') }, ...project.team.map((m) => ({ value: m.userId, label: nameOf(m.userId) }))]} />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="sg-desc">{t('projects.fields.description')}</Label>
          <Input id="sg-desc" {...form.register('description')} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="sg-date">{t('projects.install.stageCols.planned')}</Label>
          <Controller control={form.control} name="plannedDate" render={({ field }) => <DateField id="sg-date" value={field.value} onChange={field.onChange} aria-invalid={!!errors.plannedDate} />} />
        </div>
        <div className="grid grid-cols-[1fr_6rem] gap-3">
          <div className="space-y-2">
            <Label htmlFor="sg-status">{t('projects.install.columns.status')}</Label>
            <FormSelect id="sg-status" control={form.control} name="status" options={STAGE_STATUSES.map((s) => ({ value: s, label: t(`projects.stageStatus.${s}`) }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sg-comp">%</Label>
            <Input id="sg-comp" type="number" min={0} max={100} inputMode="numeric" aria-label={t('projects.install.stageCols.completion')} {...form.register('completion')} />
          </div>
        </div>
        {errors.completion ? <p className="text-xs text-destructive sm:col-span-2">{errors.completion.message}</p> : null}
        <fieldset className="space-y-2 sm:col-span-2">
          <legend className="mb-1 text-sm font-medium">{t('projects.install.stageCols.tasks')}</legend>
          {candidates.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('projects.install.form.noTasks')}</p>
          ) : (
            <Controller
              control={form.control}
              name="taskIds"
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {candidates.map((x) => {
                    const on = field.value.includes(x.id)
                    return (
                      <button
                        key={x.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() => field.onChange(on ? field.value.filter((id) => id !== x.id) : [...field.value, x.id])}
                        className={cn('inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', on ? 'border-primary/40 bg-primary/10 text-primary' : 'bg-card text-muted-foreground hover:bg-muted')}
                      >
                        {on ? <Check className="size-3.5 shrink-0" /> : null}
                        <span className="truncate">
                          {x.key} · {x.title}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            />
          )}
        </fieldset>
      </div>
    </Modal>
  )
}
